/**
 * Phase 4: The Celebration
 *
 * Full celebration mode with smart confetti patterns
 * and the glass achievement card.
 */

import { useEffect, useState, useRef, useMemo } from 'react';
import { Box } from '@mui/material';
import { motion } from 'framer-motion';
import SmartConfetti from '../effects/SmartConfetti';
import GlassCard from '../effects/GlassCard';
import { genesisAudio } from '../audio/AudioController';
import { GENESIS_COLORS } from '../types';

interface PhaseCelebrationProps {
  /** Whether this phase is active */
  active: boolean;
  /** Callback when phase completes */
  onComplete: () => void;
  /** Duration in ms */
  duration?: number;
}

const PhaseCelebration = ({ active, onComplete, duration = 7000 }: PhaseCelebrationProps) => {
  const [showConfetti, setShowConfetti] = useState(false);
  const [showCard, setShowCard] = useState(false);
  const hasStartedRef = useRef(false);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    if (!active || hasStartedRef.current) return;
    hasStartedRef.current = true;

    // Play combined celebration track
    genesisAudio.play();

    // Start confetti immediately
    setShowConfetti(true);

    // Show card after brief delay
    const t1 = setTimeout(() => {
      setShowCard(true);
    }, 600);

    // Schedule completion
    const t2 = setTimeout(() => {
      setShowCard(false);
      const t3 = setTimeout(() => {
        setShowConfetti(false);
        onComplete();
      }, 500);
      timersRef.current.push(t3);
    }, duration - 500);

    timersRef.current.push(t1, t2);

    return () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };
  }, [active, duration, onComplete]);

  // Reset when deactivated
  useEffect(() => {
    if (!active) {
      hasStartedRef.current = false;
      setShowConfetti(false);
      setShowCard(false);
    }
  }, [active]);

  // Pre-compute ambient sparkle properties once
  const ambientSparkles = useMemo(
    () =>
      Array.from({ length: 30 }).map(() => ({
        x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1000),
        y: Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 800),
        width: 8 + Math.random() * 8,
        height: 8 + Math.random() * 8,
        borderRadius: Math.random() > 0.5 ? '50%' : '2px',
        color: GENESIS_COLORS.confetti[Math.floor(Math.random() * GENESIS_COLORS.confetti.length)],
        duration: 2 + Math.random() * 2,
        delay: Math.random() * 3,
      })),
    [],
  );

  if (!active) return null;

  return (
    <>
      {/* Background dim with blur */}
      <Box
        component={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        sx={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(8px)',
          zIndex: 9998,
        }}
      />

      {/* Smart confetti system */}
      <SmartConfetti active={showConfetti} />

      {/* Glass achievement card */}
      <GlassCard visible={showCard} />

      {/* Ambient sparkles */}
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 10000,
          overflow: 'hidden',
        }}
      >
        {ambientSparkles.map((sparkle, i) => (
          <Box
            key={i}
            component={motion.div}
            initial={{
              x: sparkle.x,
              y: sparkle.y,
              scale: 0,
              opacity: 0,
            }}
            animate={{
              scale: [0, 1, 0],
              opacity: [0, 1, 0],
              rotate: [0, 180, 360],
            }}
            transition={{
              duration: sparkle.duration,
              repeat: Infinity,
              delay: sparkle.delay,
              ease: 'easeInOut',
            }}
            sx={{
              position: 'absolute',
              width: sparkle.width,
              height: sparkle.height,
              borderRadius: sparkle.borderRadius,
              background: sparkle.color,
              boxShadow: '0 0 10px currentColor',
            }}
          />
        ))}
      </Box>
    </>
  );
};

export default PhaseCelebration;
